import { selector, selectorFamily } from 'recoil'

import { ContainersAPI, TCargo } from '../api'
import {
  cargoByOrderNumberArguments,
  cargoByOrderNumberRequestId,
  containersByWarehouseRequestId,
  containersTypeByWarehouseRequestId,
  containersTypeWarehouseArguments,
  containersWarehouseArguments,
  emptyContainersIdsRequestId,
  emptyContainersTransferArguments,
  emptyContainersTypeTransferRequestId,
  warehouseFilters,
} from './atom'
import {
  Cargo,
  Container,
  ContainerType,
  convertContainerStatus,
  convertToTags,
  EmptyContainers,
  EmptyContainer,
} from './types'
import { millisecondsToDate } from 'utils/dateformat'
import { FilterAPI } from '../../filters/api/api'
import { Product } from '../../filters/store'

const mapCargo = (list: TCargo[], products: Product[] = []): Cargo[] =>
  list.map((el): Cargo => {
    const containers = el.containers.map((container): Container => ({
      id: container.id,
      name: container.name,
      status: convertContainerStatus(container.status),
      sealNumber: container.sealNumber,
    }))

    return {
      id: el.id,
      orderNumber: el.orderNumber,
      containers,
      tags: convertToTags(el.tags),
      comments: el.comments,
      date: millisecondsToDate(el.dateMilliseconds),
      items: el.items,
      product: products.find((p) => p.slug === el.product)?.title ?? '',
    }
  })

export const listProducts = selector({
  key: 'listProducts',
  get: async () => (await FilterAPI.listProducts()) as Product[],
})

export const getAsyncContainersByWarehouses = selector<Cargo[]>({
  key: 'getAsyncContainersByWarehouses',
  get: async ({ get }) => {
    // NOTE: Решение для state force refresh.
    // https://recoiljs.org/docs/guides/asynchronous-data-queries/#use-a-request-id.
    get(containersByWarehouseRequestId)
    const args = get(containersWarehouseArguments)
    const filters = get(warehouseFilters)

    if (args.id === 0) return []

    const res = await ContainersAPI.listByWarehouse(args.id, filters)

    let cargo = mapCargo(res, get(listProducts))

    if (filters.orderNumber > 0) {
      cargo = cargo.filter((el: Cargo) => filters.orderNumber === el.orderNumber)
    }

    if (filters.containerSeal > 0) {
      cargo = cargo.filter(
        (el: Cargo) =>
          el.containers.findIndex((container) => +container.sealNumber === filters.containerSeal) >=
          0
      )
    }

    return cargo
  },
})

export const getAsyncCargoByOrderNumber = selector<Cargo[]>({
  key: 'getAsyncCargoByOrderNumber',
  get: async ({ get }) => {
    // NOTE: state force refresh.
    get(cargoByOrderNumberRequestId)
    const orderNumber = get(cargoByOrderNumberArguments)

    if (!orderNumber) return []

    const res = await ContainersAPI.findCargoByOrderNumber(orderNumber)

    return mapCargo(res, get(listProducts))
  },
})

// NOTE: Вопрос, тут что то можно придумать чтобы вот так тупо не дублировать
// селекторы?
export const getAsyncContainerTypes = selector<ContainerType[]>({
  key: 'getAsyncContainerTypes',
  get: async ({ get }) => {
    // NOTE: Решение для state force refresh.
    // https://recoiljs.org/docs/guides/asynchronous-data-queries/#use-a-request-id.
    get(containersTypeByWarehouseRequestId)
    const args = get(containersTypeWarehouseArguments)

    if (args.id === 0) return []

    const res = await ContainersAPI.warehouseStock(args.id)

    return res as ContainerType[]
  },
})

export const getContainerTypeById = selectorFamily<ContainerType, string>({
  key: 'getContainerTypeById',
  get:
    (containerTypeId: string) =>
    ({ get }) => {
      const list = get(getAsyncContainerTypes)

      return new Map(list.map((el) => [el.id, el])).get(containerTypeId)
    },
})

export const getAsyncEmptyContainerTypes = selector<ContainerType[]>({
  key: 'getAsyncEmptyContainerTypes',
  get: async ({ get }) => {
    // NOTE: Решение для state force refresh.
    // https://recoiljs.org/docs/guides/asynchronous-data-queries/#use-a-request-id.
    get(emptyContainersTypeTransferRequestId)
    const args = get(emptyContainersTransferArguments)

    if (args.from.id === 0) return []

    const res = await ContainersAPI.warehouseStock(args.from.id)

    return res as ContainerType[]
  },
})

export const getEmptyContainerTypeById = selectorFamily<ContainerType, string>({
  key: 'getEmptyContainerTypeById',
  get:
    (containerTypeId: string) =>
    ({ get }) => {
      const list = get(getAsyncEmptyContainerTypes)

      return new Map(list.map((el) => [el.id, el])).get(containerTypeId)
    },
})

// NOTE: Запрос дублируется если сделать F5 в браузере. При нормальной работе, запрос выполняется
// только 1 раз.
export const getAsyncEmptyContainerIdsByWarehouse = selectorFamily<string[], string>({
  key: 'getAsyncEmptyContainersByWarehouse',
  get:
    (containerTypeId: string) =>
    async ({ get }) => {
      // NOTE: Решение для state force refresh.
      // https://recoiljs.org/docs/guides/asynchronous-data-queries/#use-a-request-id.
      get(emptyContainersIdsRequestId)
      const args = get(emptyContainersTransferArguments)

      if (args.from.id === 0) return []

      const res = await ContainersAPI.listEmptyContainers(args.from.id)
      const values = new Map(
        res.map((el: EmptyContainers) => [el.containerTypeId, el.emptyContainers])
      ).get(containerTypeId)

      if (values === undefined) return []

      return values.map((el) => el.id)
    },
})

export const getAsyncEmptyContainers = selector<EmptyContainer[]>({
  key: 'getAsyncEmptyContainers',
  get: async ({ get }) => {
    // NOTE: Решение для state force refresh.
    // https://recoiljs.org/docs/guides/asynchronous-data-queries/#use-a-request-id.
    get(emptyContainersTypeTransferRequestId)
    const args = get(emptyContainersTransferArguments)

    if (args.from.id === 0) return []

    const res = await ContainersAPI.listEmptyContainers(args.from.id)

    return res.flatMap((el: EmptyContainers) => el.emptyContainers)
  },
})

/* eslint-enable prettier/prettier */
